import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Plus, Trash, PencilSimple, Phone, LinkSimple, X,
  Warning, House, User, Users, Package,
} from "@phosphor-icons/react";

const KIND_LABELS = {
  awr: "AWR · Water/Sewer Line",
  home_warranty: "Home Warranty",
  landlord_dwelling: "Landlord Dwelling",
  landlord_liability: "Landlord Liability",
  landlord_umbrella: "Landlord Umbrella",
  renters: "Renter's Insurance",
  sewer_line: "Sewer Line",
  service_line: "Service Line",
  flood: "Flood",
  other: "Other",
};

const SCOPE_LABELS = {
  property: "Property",
  landlord: "Landlord Portfolio",
  tenant: "Tenant / Renter",
  portfolio: "Entire Portfolio",
};

const SCOPE_ICON = {
  property: House,
  landlord: Users,
  tenant: User,
  portfolio: Package,
};

export default function AdminInsurance() {
  const [policies, setPolicies] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [scopeFilter, setScopeFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [p, c] = await Promise.all([
        api.get(`/insurance-policies${scopeFilter ? `?scope=${scopeFilter}` : ""}`),
        catalog ? Promise.resolve({ data: catalog }) : api.get("/insurance-policies/catalog"),
      ]);
      setPolicies(p.data || []);
      if (!catalog) setCatalog(c.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [scopeFilter]);

  const remove = async (p) => {
    if (!window.confirm(`Deactivate "${p.provider_name}"? (soft-delete — audit history preserved)`)) return;
    try { await api.delete(`/insurance-policies/${p.id}`); toast.success("Deactivated"); load(); }
    catch { toast.error("Failed"); }
  };

  const money = (c) => c ? `$${(c/100).toFixed(2)}` : "—";

  return (
    <AdminShell>
      <div className="space-y-6" data-testid="admin-insurance-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <ShieldCheck weight="fill" className="text-cyan-500"/> Insurance & Warranty
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Track every policy — <b>AWR</b>, home warranties, landlord dwelling, renter's insurance, sewer/service lines.
              Jobs at covered properties will show a red banner and require the claim to be filed before a tech can clock in.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="add-policy-btn"
            className="h-11 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-sm shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-2">
            <Plus size={14}/> Add Policy
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => setScopeFilter("")} data-testid="filter-scope-all"
            className={`h-8 px-3 border-2 border-slate-900 font-black uppercase tracking-widest text-[10px] ${scopeFilter === "" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-100"}`}>All</button>
          {["property", "landlord", "tenant", "portfolio"].map(s => (
            <button key={s} onClick={() => setScopeFilter(s)} data-testid={`filter-scope-${s}`}
              className={`h-8 px-3 border-2 border-slate-900 font-black uppercase tracking-widest text-[10px] ${scopeFilter === s ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-100"}`}>
              {SCOPE_LABELS[s]}
            </button>
          ))}
        </div>

        {busy && <div className="text-sm text-slate-500">Loading…</div>}
        {!busy && policies.length === 0 && (
          <div className="p-8 bg-cyan-50 border-4 border-cyan-500 text-center" data-testid="policies-empty">
            <ShieldCheck size={40} className="mx-auto text-cyan-500"/>
            <div className="mt-2 font-black uppercase tracking-widest text-cyan-900">No policies yet</div>
            <div className="text-xs text-slate-600 mt-1">Add your first — <b>AWR</b>, First American Home Warranty, or a landlord dwelling policy.</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {policies.map(p => {
            const Icon = SCOPE_ICON[p.scope] || Package;
            return (
              <div key={p.id} className="rugged-card p-4 space-y-2" data-testid={`policy-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 h-6 bg-cyan-100 text-cyan-800 text-[10px] font-black uppercase tracking-widest">
                        <Icon size={11}/> {SCOPE_LABELS[p.scope] || p.scope}
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{KIND_LABELS[p.kind] || p.kind}</span>
                    </div>
                    <div className="mt-2 font-black text-base" data-testid={`policy-provider-${p.id}`}>{p.provider_name}</div>
                    {p.policy_number && <div className="text-xs text-slate-500">Policy #: {p.policy_number}</div>}
                  </div>
                </div>

                {p.property_address && <div className="text-xs text-slate-600 flex items-center gap-1"><House size={11}/> {p.property_address}</div>}
                {p.landlord_name && <div className="text-xs text-slate-600 flex items-center gap-1"><Users size={11}/> {p.landlord_name}</div>}
                {p.tenant_email && <div className="text-xs text-slate-600 flex items-center gap-1"><User size={11}/> {p.tenant_email}</div>}

                {p.coverage_summary && <p className="text-xs text-slate-700 line-clamp-2">{p.coverage_summary}</p>}

                {p.covered_categories?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {p.covered_categories.slice(0, 6).map(c => (
                      <span key={c} className="px-2 h-5 bg-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-700 leading-5">{c.replace(/_/g, " ")}</span>
                    ))}
                    {p.covered_categories.length > 6 && (
                      <span className="text-[10px] text-slate-500 self-center">+{p.covered_categories.length - 6}</span>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-3 text-[11px] text-slate-600 pt-1">
                  {p.claim_phone && <span className="inline-flex items-center gap-1"><Phone size={11}/> {p.claim_phone}</span>}
                  {p.claim_portal_url && <a href={p.claim_portal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-700 hover:underline"><LinkSimple size={11}/> Portal</a>}
                  {p.deductible_cents > 0 && <span>Deduct: <b>{money(p.deductible_cents)}</b></span>}
                  {p.service_fee_cents > 0 && <span>Trip fee: <b>{money(p.service_fee_cents)}</b></span>}
                  {p.expiration_date && <span>Exp: <b>{p.expiration_date}</b></span>}
                </div>

                {p.warranty_contractor?.name && (
                  <div className="mt-2 p-2 bg-fuchsia-50 border-l-4 border-fuchsia-500 text-xs">
                    <b>Warranty vendor:</b> {p.warranty_contractor.name}
                    {p.warranty_contractor.phone && ` · ${p.warranty_contractor.phone}`}
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t border-slate-200">
                  <button onClick={() => setEditing(p)} data-testid={`policy-edit-${p.id}`}
                    className="h-8 px-2 border-2 border-slate-900 font-black uppercase tracking-widest text-[10px] hover:bg-slate-100 inline-flex items-center gap-1">
                    <PencilSimple size={10}/> Edit
                  </button>
                  <button onClick={() => remove(p)} data-testid={`policy-remove-${p.id}`}
                    className="h-8 px-2 border-2 border-red-500 text-red-600 font-black uppercase tracking-widest text-[10px] hover:bg-red-50 inline-flex items-center gap-1 ml-auto">
                    <Trash size={10}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(showNew || editing) && catalog && (
        <PolicyModal
          initial={editing}
          catalog={catalog}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </AdminShell>
  );
}


function PolicyModal({ initial, catalog, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    scope: "property", kind: "awr", provider_name: "",
    policy_number: "", coverage_summary: "", covered_categories: [],
    deductible_cents: 0, service_fee_cents: 0, expiration_date: "",
    claim_phone: "", claim_email: "", claim_portal_url: "",
    property_address: "", landlord_id: "", tenant_email: "",
    warranty_contractor: { name: "", phone: "", email: "" },
    notes: "", active: true,
  });
  const [busy, setBusy] = useState(false);
  const [landlords, setLandlords] = useState([]);

  useEffect(() => {
    api.get("/landlords").then(r => setLandlords(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (preset) => {
    setForm(f => ({
      ...f,
      kind: preset.kind,
      provider_name: preset.provider_name,
      covered_categories: preset.typical_coverage || [],
      deductible_cents: preset.typical_deductible_cents || 0,
      service_fee_cents: preset.typical_service_fee_cents || 0,
      claim_phone: preset.claim_phone || "",
      claim_portal_url: preset.claim_portal_url || "",
      coverage_summary: preset.notes || f.coverage_summary,
    }));
    toast.success(`Loaded ${preset.provider_name} defaults`);
  };

  const toggleCategory = (c) => {
    setForm(f => {
      const has = (f.covered_categories || []).includes(c);
      return { ...f, covered_categories: has
        ? f.covered_categories.filter(x => x !== c)
        : [...(f.covered_categories || []), c] };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        deductible_cents: parseInt(form.deductible_cents) || 0,
        service_fee_cents: parseInt(form.service_fee_cents) || 0,
      };
      if (initial) {
        await api.patch(`/insurance-policies/${initial.id}`, payload);
      } else {
        await api.post("/insurance-policies", payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="policy-modal">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-4 bg-cyan-600 text-white flex items-start justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{initial ? "Edit" : "New"}</div>
            <div className="text-lg font-black uppercase tracking-widest">Insurance Policy</div>
          </div>
          <button onClick={onClose} data-testid="policy-close-btn" className="p-1 hover:bg-cyan-700"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-4">
          {!initial && catalog?.providers && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">Quick-start from a known provider</div>
              <div className="flex flex-wrap gap-2">
                {catalog.providers.map((preset, i) => (
                  <button key={i} onClick={() => applyPreset(preset)}
                    data-testid={`preset-${preset.provider_name.replace(/\s/g, '_')}`}
                    className="h-9 px-3 border-2 border-cyan-500 text-cyan-700 font-black uppercase tracking-widest text-[10px] hover:bg-cyan-50">
                    {preset.display}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Scope</label>
              <select value={form.scope} onChange={e => setForm({...form, scope: e.target.value})} data-testid="policy-scope"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm">
                {Object.entries(SCOPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Kind</label>
              <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})} data-testid="policy-kind"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm">
                {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Provider</label>
            <input value={form.provider_name} onChange={e => setForm({...form, provider_name: e.target.value})} data-testid="policy-provider"
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm" placeholder="American Water Resources"/>
          </div>

          {form.scope === "property" && (
            <>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Property Address</label>
                <input value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})} data-testid="policy-address"
                  className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm" placeholder="1906 E Oakdale St"/>
              </div>
              {!initial && (
                <BulkApplySection
                  landlords={landlords}
                  template={form}
                  onDone={(count) => { toast.success(`Bulk applied to ${count} properties`); onSaved(); }}
                />
              )}
            </>
          )}
          {form.scope === "landlord" && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Landlord</label>
              <select value={form.landlord_id} onChange={e => setForm({...form, landlord_id: e.target.value})} data-testid="policy-landlord"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm">
                <option value="">— pick landlord —</option>
                {landlords.map(ll => <option key={ll.id} value={ll.id}>{ll.name}</option>)}
              </select>
            </div>
          )}
          {form.scope === "tenant" && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Tenant email</label>
              <input value={form.tenant_email} onChange={e => setForm({...form, tenant_email: e.target.value})} data-testid="policy-tenant-email"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm" placeholder="jane@renter.com"/>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Policy #</label>
              <input value={form.policy_number} onChange={e => setForm({...form, policy_number: e.target.value})}
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Expires</label>
              <input value={form.expiration_date} onChange={e => setForm({...form, expiration_date: e.target.value})}
                placeholder="MM/DD/YYYY"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Coverage summary</label>
            <textarea value={form.coverage_summary} onChange={e => setForm({...form, coverage_summary: e.target.value})} rows={3}
              className="mt-1 w-full p-3 border-2 border-slate-300 text-sm"/>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Covered categories ({(form.covered_categories || []).length} picked)</label>
            <div className="mt-1 flex flex-wrap gap-1 p-2 border-2 border-slate-300 max-h-40 overflow-y-auto">
              {(catalog?.coverage_categories || []).map(c => {
                const active = (form.covered_categories || []).includes(c);
                return (
                  <button key={c} onClick={() => toggleCategory(c)} type="button"
                    data-testid={`coverage-${c}`}
                    className={`px-2 h-7 text-[10px] font-black uppercase tracking-widest border-2 ${active ? "bg-cyan-600 text-white border-cyan-600" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                    {c.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Deductible ($)</label>
              <input type="number" value={(form.deductible_cents || 0) / 100}
                onChange={e => setForm({...form, deductible_cents: Math.round((parseFloat(e.target.value) || 0) * 100)})}
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Trip fee ($)</label>
              <input type="number" value={(form.service_fee_cents || 0) / 100}
                onChange={e => setForm({...form, service_fee_cents: Math.round((parseFloat(e.target.value) || 0) * 100)})}
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Claim phone</label>
              <input value={form.claim_phone} onChange={e => setForm({...form, claim_phone: e.target.value})}
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Claim portal URL</label>
              <input value={form.claim_portal_url} onChange={e => setForm({...form, claim_portal_url: e.target.value})}
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
          </div>

          <div className="border-2 border-fuchsia-200 p-3 bg-fuchsia-50">
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-800 mb-2">
              Warranty vendor (their contractor — optional)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input value={form.warranty_contractor?.name || ""}
                onChange={e => setForm({...form, warranty_contractor: {...(form.warranty_contractor || {}), name: e.target.value}})}
                placeholder="Vendor name"
                className="h-9 px-2 border-2 border-slate-300 text-xs"/>
              <input value={form.warranty_contractor?.phone || ""}
                onChange={e => setForm({...form, warranty_contractor: {...(form.warranty_contractor || {}), phone: e.target.value}})}
                placeholder="Phone"
                className="h-9 px-2 border-2 border-slate-300 text-xs"/>
              <input value={form.warranty_contractor?.email || ""}
                onChange={e => setForm({...form, warranty_contractor: {...(form.warranty_contractor || {}), email: e.target.value}})}
                placeholder="Email"
                className="h-9 px-2 border-2 border-slate-300 text-xs"/>
            </div>
          </div>

          <button onClick={save} disabled={busy} data-testid="policy-save-btn"
            className="w-full h-12 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : (initial ? "Save Changes" : "Create Policy")}
          </button>
        </div>
      </div>
    </div>
  );
}


function BulkApplySection({ landlords, template, onDone }) {
  const [expanded, setExpanded] = useState(false);
  const [properties, setProperties] = useState([]);
  const [landlordFilter, setLandlordFilter] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!expanded) return;
    api.get("/properties").then(r => setProperties(r.data || []))
      .catch(() => toast.error("Failed to load properties"));
  }, [expanded]);

  const filtered = properties.filter(p => {
    if (landlordFilter && p.landlord_id !== landlordFilter) return false;
    if (search && !p.address?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selectAll = () => {
    const map = {};
    filtered.forEach(p => { map[p.address] = true; });
    setSelected(map);
  };
  const clearAll = () => setSelected({});

  const selectedAddrs = Object.keys(selected).filter(k => selected[k]);

  const apply = async () => {
    if (!template.kind || !template.provider_name) {
      toast.error("Fill in Kind + Provider on the main form first");
      return;
    }
    if (selectedAddrs.length === 0) {
      toast.error("Pick at least one property");
      return;
    }
    if (!window.confirm(`Apply "${template.provider_name}" (${template.kind}) to ${selectedAddrs.length} properties?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/insurance-policies/bulk-apply", {
        template: {
          kind: template.kind,
          provider_name: template.provider_name,
          policy_number: template.policy_number || "",
          coverage_summary: template.coverage_summary || "",
          covered_categories: template.covered_categories || [],
          deductible_cents: parseInt(template.deductible_cents) || 0,
          service_fee_cents: parseInt(template.service_fee_cents) || 0,
          expiration_date: template.expiration_date || "",
          claim_phone: template.claim_phone || "",
          claim_email: template.claim_email || "",
          claim_portal_url: template.claim_portal_url || "",
          warranty_contractor: template.warranty_contractor || null,
          notes: template.notes || "",
        },
        property_addresses: selectedAddrs,
      }, { timeout: 60000 });
      toast.success(`Created ${data.created} policies · skipped ${data.skipped_duplicates} duplicates`);
      onDone(data.created);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk apply failed"));
    } finally { setBusy(false); }
  };

  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} data-testid="bulk-apply-toggle"
        className="w-full h-11 border-2 border-fuchsia-500 text-fuchsia-700 hover:bg-fuchsia-50 font-black uppercase tracking-widest text-xs">
        + Also apply this policy to MORE properties (bulk)
      </button>
    );
  }

  return (
    <div className="p-3 border-4 border-fuchsia-500 bg-fuchsia-50 space-y-2" data-testid="bulk-apply-section">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-900">
          Bulk Apply · {selectedAddrs.length} selected
        </div>
        <button type="button" onClick={() => setExpanded(false)} className="text-fuchsia-700 text-xs">Close</button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select value={landlordFilter} onChange={e => setLandlordFilter(e.target.value)} data-testid="bulk-landlord-filter"
          className="h-9 px-2 border-2 border-slate-300 text-xs">
          <option value="">All landlords</option>
          {landlords.map(ll => <option key={ll.id} value={ll.id}>{ll.name}</option>)}
        </select>
        <input list="known-addresses" autoComplete="off" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search address…" data-testid="bulk-address-search"
          className="h-9 px-2 border-2 border-slate-300 text-xs"/>
      </div>

      <div className="flex gap-2 text-[10px]">
        <button type="button" onClick={selectAll} data-testid="bulk-select-all" className="h-7 px-2 border-2 border-fuchsia-700 text-fuchsia-700 font-black uppercase tracking-widest hover:bg-fuchsia-100">Select all ({filtered.length})</button>
        <button type="button" onClick={clearAll} data-testid="bulk-clear-all" className="h-7 px-2 border-2 border-slate-500 text-slate-700 font-black uppercase tracking-widest hover:bg-slate-100">Clear</button>
      </div>

      <div className="max-h-52 overflow-y-auto border-2 border-slate-300 bg-white">
        {filtered.length === 0 && <div className="p-3 text-xs text-slate-500 text-center">No properties match</div>}
        {filtered.map(p => (
          <label key={p.address} className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 hover:bg-fuchsia-50 text-xs cursor-pointer" data-testid={`bulk-prop-${p.address}`}>
            <input type="checkbox" checked={!!selected[p.address]}
              onChange={e => setSelected(s => ({...s, [p.address]: e.target.checked}))}/>
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">{p.address}</div>
              {p.landlord_name && <div className="text-[10px] text-slate-500 truncate">{p.landlord_name}</div>}
            </div>
          </label>
        ))}
      </div>

      <button type="button" onClick={apply} disabled={busy || selectedAddrs.length === 0} data-testid="bulk-apply-submit"
        className="w-full h-10 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 shadow-[2px_2px_0_#0f172a]">
        {busy ? "Applying…" : `Apply "${template.provider_name || 'policy'}" to ${selectedAddrs.length} properties`}
      </button>
    </div>
  );
}
